import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hand } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { cardTheme } from '../../styles/cardTheme';
import TeacherCard, { TeacherCardData } from './TeacherCard';
import ScrollReveal from '../common/ScrollReveal';

interface TeachersSectionProps {
  id?: string;
  className?: string;
  hint?: string;
  title?: string;
  subtitle?: string;
  badge?: string;
  /** When true, renders inner layout only (for HomeSectionCard wrapper) */
  embedded?: boolean;
  /** Base path for teacher links — default `/teacher/` */
  teacherPathPrefix?: string;
}

function getTeacherSpeciality(_name: string, speciality: string): string {
  return speciality;
}

const TeachersSkeleton: React.FC = () => (
  <div
    className="mx-auto grid w-full max-w-4xl grid-cols-2 items-stretch justify-center gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4"
    aria-hidden
  >
    {[1, 2, 3, 4].map((i) => (
      <div
        key={i}
        className={`mx-auto flex h-full w-full max-w-[15rem] animate-pulse flex-col ${cardTheme.shell}`}
      >
        <div className="aspect-[3/4] w-full bg-[#EFE8DC]" />
        <div className="flex flex-1 flex-col space-y-2 border-t-[3px] border-t-[#C09048] bg-[#FAF7F0] p-3">
          <div className="h-4 w-3/4 rounded bg-[#E5DCCF]" />
          <div className="h-3 w-full rounded bg-[#E5DCCF]" />
          <div className="mt-auto h-10 w-full rounded-full bg-[#E5DCCF]" />
        </div>
      </div>
    ))}
  </div>
);

const TeachersSection: React.FC<TeachersSectionProps> = ({
  id = 'teachers-section',
  className = 'relative py-12 sm:py-16 lg:py-20 bg-[#F5F1E8]',
  hint,
  title = 'مدرسين أوائل مصر',
  subtitle = 'اختَر مدرّسك وابدأ رحلتك التعليمية في كل المواد وكل المراحل.',
  badge = 'المدرسون والمواد',
  embedded = false,
  teacherPathPrefix = '/teacher/',
}) => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<TeacherCardData[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  const handleTeacherClick = useCallback(
    (teacherId: string) => {
      const prefix = teacherPathPrefix.endsWith('/')
        ? teacherPathPrefix
        : `${teacherPathPrefix}/`;
      navigate(`${prefix}${teacherId}`);
    },
    [navigate, teacherPathPrefix]
  );

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoadingTeachers(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            fullName: string;
            imagePath?: string;
            department?: string;
            biography?: string;
            yearsOfExperience?: number;
            active: boolean;
          }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data?.data) {
          const teachersData = response.data.data
            .filter((teacher) => teacher.active)
            .map((teacher) => {
              const imageUrl = teacher.imagePath?.startsWith('http')
                ? teacher.imagePath
                : teacher.imagePath
                ? `${BASE_URL}${teacher.imagePath.startsWith('/') ? teacher.imagePath.slice(1) : teacher.imagePath}`
                : '/default-teacher.jpg';

              return {
                id: teacher.id,
                name: teacher.fullName,
                image: imageUrl,
                speciality: getTeacherSpeciality(
                  teacher.fullName,
                  teacher.department || 'مدرس متخصص'
                ),
                biography: teacher.biography,
              };
            });

          setTeachers(teachersData);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
      } finally {
        setLoadingTeachers(false);
      }
    };

    fetchTeachers();
  }, []);

  const content = (
    <div className={embedded ? 'w-full' : 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8'}>
      <ScrollReveal variant="up" className="mb-8 max-w-3xl md:mb-10">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-[#C09048]" aria-hidden />
          <p className="text-sm font-semibold text-[#A87824]">{badge}</p>
        </div>
        <h2
          id="azhar-teachers-heading"
          className={`text-[clamp(1.6rem,1.5vw+1.1rem,2.35rem)] font-extrabold font-cairo-bold ${cardTheme.title}`}
        >
          {title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B82] sm:text-base sm:leading-7">
          {subtitle}
        </p>
        {hint && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-[#E0C99A]/80 bg-[#F8F1E4]/90 px-4 py-3 text-sm font-semibold leading-relaxed text-[#A87824] shadow-sm">
            <Hand className="h-5 w-5 shrink-0 text-[#C09048]" />
            {hint}
          </p>
        )}
      </ScrollReveal>

      {loadingTeachers ? (
        <TeachersSkeleton />
      ) : teachers.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-[#5A6B82]">لا يوجد مدرسين متاحين حالياً</p>
        </div>
      ) : (
        <>
          <div
            className="mx-auto grid w-full max-w-4xl grid-cols-2 items-stretch justify-center gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[15rem]"
            role="list"
            aria-label="المدرسون"
          >
            {teachers.map((teacher, index) => (
              <ScrollReveal key={teacher.id} variant="up" delay={index * 70} className="h-full w-full max-w-[15rem]">
                <TeacherCard
                  teacher={teacher}
                  onClick={handleTeacherClick}
                  teacherPathPrefix={teacherPathPrefix}
                />
              </ScrollReveal>
            ))}
          </div>
          <div aria-live="polite" className="sr-only">
            معروض {teachers.length} من {teachers.length} مدرسين
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div id={id} className="relative z-10">
        {content}
      </div>
    );
  }

  return (
    <section id={id} className={`relative z-10 ${className}`}>
      {content}
    </section>
  );
};

export default TeachersSection;
